// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { InputBox } from '@imprivata-cloud/components';
import { CodingContext, getRSAPublicKey } from '@imprivata-cloud/dataprivacy';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import ContinueButton from '@components/continueButton/ContinueButton';
import { useChangePassword } from '@selfservice/hooks/useChangePassword';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '.././../../../errorHandler/errors';
import { ErrorCode, ErrorMessage } from '@errorHandler/constants';
import { _getUrl } from '@api/utils';
import { SelfServicePath } from '@selfservice/SelfServiceRoutes';

import './IdentityPassword.less';

enum fieldName {
  PASSWORD = 'password',
  CONFIRM_PASSWORD = 'confirmPassword',
}

interface AuthnPassword {
  [fieldName.PASSWORD]: string;
  [fieldName.CONFIRM_PASSWORD]: string;
}

const TRANSLATION_ROOT = 'self-service.auth-method.password';

const IdentityPassword = () => {
  const [form] = Form.useForm<AuthnPassword>();
  const { t } = useTranslation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { emitError } = useErrorContext();
  const navigate = useNavigate();
  const { mutate: changePassword } = useChangePassword();

  const updateButtonState = () => {
    const password = form.getFieldValue(fieldName.PASSWORD) || '';
    const confirmPassword = form.getFieldValue(fieldName.CONFIRM_PASSWORD) || '';
    const isValid =
      password.length >= 16 &&
      password.length <= 64 &&
      confirmPassword.length >= 16 &&
      confirmPassword.length <= 64 &&
      password === confirmPassword;

    setIsButtonDisabled(!isValid);
  };

  const handleSubmit = async (values: AuthnPassword) => {
    const { metadataUrl } = _getUrl({ metadataUrl: { sub: 'metadata.app' } });
    const publicDev = await getRSAPublicKey(`${metadataUrl}/data-privacy/public.json`);
    const codingCtx = await CodingContext.createContextV2(publicDev.key, publicDev.version);
    const password = codingCtx.encryptString(values.password);
    changePassword(
      {
        changePasswordRequest: { newPassword: password },
        codingContext: codingCtx,
      },
      {
        onSuccess: () => {
          navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
        },
        onError: (e) => {
          console.log('Error changing password:', e);
          emitError(AppError.fromMessage(ErrorMessage.SYSTEM_ERROR, ErrorCode.UNKNOWN));
        },
      },
    );
  };

  const handleDoThisLater = () => {
    navigate(SelfServicePath.SELF_SERVICE_LANDING_PAGE);
  };

  return (
    <div className="identity-password">
      <div className="title">{t(`${TRANSLATION_ROOT}.title`)}</div>
      <div className="sub-title">{t(`${TRANSLATION_ROOT}.sub-title`)}</div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        validateTrigger={[]}
        onValuesChange={updateButtonState}
      >
        {/* PASSWORD */}
        <Form.Item
          name={fieldName.PASSWORD}
          rules={[{ required: true, min: 16, max: 64 }]}
          extra={t(`${TRANSLATION_ROOT}.minimum-password-length`)}
        >
          <InputBox
            label={t(`${TRANSLATION_ROOT}.password`)}
            autoFocus
            required
            type="password"
            aria-label="password"
          />
        </Form.Item>

        {/* CONFIRM PASSWORD */}
        <Form.Item
          name={fieldName.CONFIRM_PASSWORD}
          rules={[{ required: true, min: 16, max: 64 }]}
          extra={t(`${TRANSLATION_ROOT}.minimum-password-length`)}
        >
          <InputBox
            label={t(`${TRANSLATION_ROOT}.confirm-password`)}
            required
            type="password"
            aria-label="confirm-password"
          />
        </Form.Item>

        {/* CONTINUE or LATER */}
        <ContinueButton
          aria-label="continue-button"
          htmlType="submit"
          disabled={isButtonDisabled}
          secondaryButton={{
            'aria-label': 'do-this-later-button',
            label: t('common.do-later'),
            onClick: handleDoThisLater,
          }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </Form>
    </div>
  );
};

export default IdentityPassword;
